// components/VideoGallery.jsx
import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X, List, Grid3X3, Search, VideoIcon, Sparkles } from 'lucide-react';

const SkeletonLoader = ({ count = 8 }) => (
  <div className="grid gap-4 sm:gap-6 grid-cols-2 lg:grid-cols-4">
    {Array.from({ length: count }).map((_, i) => (
      <div 
        key={i} 
        className="bg-white/[0.03] backdrop-blur-md rounded-2xl overflow-hidden border border-white/10 p-3 space-y-3 shadow-xl"
      >
        {/* Thumbnail Placeholder with Shimmer */}
        <div className="relative w-full h-32 sm:h-40 md:h-48 bg-white/5 rounded-xl overflow-hidden">
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.8s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
        </div>
        
        {/* Text Placeholders */}
        <div className="space-y-2 px-1">
          <div className="relative h-3.5 bg-white/10 rounded-md w-full overflow-hidden">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.8s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
          </div>
          <div className="relative h-3 bg-white/5 rounded-md w-2/3 overflow-hidden">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.8s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

export default function VideoGallery({ videos = [], loading }) {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('card');
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const videosPerPage = 12;

  const extractYouTubeId = (url) => {
    if (!url) return '';
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : url;
  };

  // Logic for filtering
  const filteredVideos = useMemo(() => {
    return videos.filter(video => {
      const lowerCaseSearch = searchTerm.toLowerCase();
      return (
        String(video.name || '').toLowerCase().includes(lowerCaseSearch) ||
        String(video.url || '').toLowerCase().includes(lowerCaseSearch)
      );
    });
  }, [videos, searchTerm]);

  // Pagination calculations
  const indexOfLastVideo = currentPage * videosPerPage;
  const indexOfFirstVideo = indexOfLastVideo - videosPerPage;
  const currentVideos = filteredVideos.slice(indexOfFirstVideo, indexOfLastVideo);
  const totalPages = Math.ceil(filteredVideos.length / videosPerPage);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Render Video Card Item
  const renderVideoCard = (video, index) => {
    const videoId = extractYouTubeId(video.url);
    const itemKey = video.key || index;
    const isHovered = hoveredVideo === itemKey;

    return (
      <motion.div
        key={itemKey}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: (index % 4) * 0.05 }}
        viewport={{ once: true }}
        onClick={() => setSelectedVideo(video)}
        onMouseEnter={() => setHoveredVideo(itemKey)}
        onMouseLeave={() => setHoveredVideo(null)}
        className="group relative bg-[#121318]/70 hover:bg-[#181a22] backdrop-blur-md rounded-2xl overflow-hidden border border-white/10 hover:border-[#bd9f67]/50 shadow-lg hover:shadow-[0_10px_30px_rgba(189,159,103,0.15)] transition-all duration-300 cursor-pointer flex flex-col justify-between"
      >
        {/* Video Preview Container */}
        <div className="relative w-full aspect-video overflow-hidden bg-black/40">
          <img
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
            src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
            alt={video.name}
            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
          />
          
          {isHovered && videoId && (
            <iframe
              className="absolute inset-0 w-full h-full pointer-events-none z-10"
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&rel=0`}
              title={video.name}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          )}

          {/* Overlay Gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20 z-10 pointer-events-none" />

          {/* Play Badge */}
          <div className="absolute inset-0 flex items-center justify-center z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="w-12 h-12 rounded-full bg-[#bd9f67] text-gray-950 flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
              <Play size={20} className="fill-current ml-1" />
            </div>
          </div>

          {video.duration && (
            <span className="absolute bottom-2.5 right-2.5 z-20 px-2 py-0.5 text-[10px] font-medium tracking-wide bg-black/80 backdrop-blur-md text-white/90 rounded-md border border-white/10">
              {video.duration}
            </span>
          )}
        </div>

        {/* Info Area */}
        <div className="p-4 flex-1 flex flex-col justify-between">
          <h4 className="text-sm font-medium text-white/90 group-hover:text-[#bd9f67] line-clamp-2 transition-colors duration-200 leading-snug">
            {video.name}
          </h4>
        </div>
      </motion.div>
    );
  };

  // Render Video Table Item
  const renderVideoTable = (videosToDisplay) => (
    <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#121318]/60 backdrop-blur-xl shadow-xl mt-4"> 
      <table className="min-w-full divide-y divide-white/10 text-xs sm:text-sm">
        <thead className="bg-black/40">
          <tr>
            <th className="px-5 py-4 text-left font-semibold text-[#bd9f67] uppercase tracking-wider w-16">#</th>
            <th className="px-5 py-4 text-left font-semibold text-[#bd9f67] uppercase tracking-wider w-28">Preview</th>
            <th className="px-5 py-4 text-left font-semibold text-[#bd9f67] uppercase tracking-wider">Title</th>
            <th className="px-5 py-4 text-center font-semibold text-[#bd9f67] uppercase tracking-wider w-24">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {videosToDisplay.map((video, index) => {
            const videoId = extractYouTubeId(video.url);
            return (
              <tr 
                key={video.key || index} 
                className="hover:bg-white/[0.04] cursor-pointer transition-colors duration-200 group" 
                onClick={() => setSelectedVideo(video)}
              >
                <td className="px-5 py-3.5 text-white/40 font-mono text-xs">{indexOfFirstVideo + index + 1}</td>
                <td className="px-5 py-3.5">
                  <div className="w-16 h-10 rounded-lg overflow-hidden border border-white/10 bg-black/50 relative">
                    <img
                      src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                      alt={video.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                </td>
                <td className="px-5 py-3.5 text-white/90 font-medium group-hover:text-[#bd9f67] transition-colors">
                  {video.name}
                </td>
                <td className="px-5 py-3.5 text-center">
                  <div className="w-8 h-8 rounded-full bg-white/5 group-hover:bg-[#bd9f67] group-hover:text-gray-950 text-white/80 flex items-center justify-center mx-auto transition-all duration-200 shadow-sm">
                    <Play size={14} className="fill-current ml-0.5" />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <section 
      id="videos" 
      className="relative py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-b from-gray-950 via-gray-900 to-black text-white min-h-[700px] overflow-hidden"
    >
      {/* Background Lighting Glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#bd9f67]/10 blur-[120px] rounded-full pointer-events-none" />
      
      <div className="max-w-7xl mx-auto relative z-10">

        {/* MAIN CONDITIONAL LOGIC */}
        {loading ? (
          <SkeletonLoader count={8} />
        ) : (
          <>
            {/* SEARCH AND TOGGLE BAR */}
            {(videos.length > 0 || searchTerm !== '') && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8 flex flex-col sm:flex-row justify-between items-center gap-4 bg-[#121318]/80 backdrop-blur-xl p-2.5 sm:p-3 rounded-2xl border border-white/10 shadow-xl"
              >
                {/* Search Field */}
                <div className="flex-1 w-full flex items-center bg-black/40 rounded-xl px-3.5 py-2.5 border border-white/10 focus-within:border-[#bd9f67] focus-within:ring-1 focus-within:ring-[#bd9f67] transition-all">
                  <Search className="text-[#bd9f67] mr-2.5 shrink-0" size={18} /> 
                  <input
                    type="text"
                    placeholder="Search videos by title..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="w-full bg-transparent text-white placeholder-white/40 outline-none text-sm"
                  />
                  {searchTerm && (
                    <button onClick={() => setSearchTerm('')} className="text-white/40 hover:text-white text-xs px-1">
                      <X size={16} />
                    </button>
                  )}
                </div>

                {/* View Toggles */}
                <div className="flex items-center gap-1.5 self-end sm:self-auto bg-black/40 p-1 rounded-xl border border-white/5"> 
                  <button 
                    onClick={() => setViewMode('card')}
                    className={`p-2 rounded-lg transition-all ${viewMode === 'card' ? 'bg-[#bd9f67] text-gray-950 shadow-md font-semibold' : 'text-white/60 hover:text-white'}`}
                    title="Grid View"
                  >
                    <Grid3X3 size={18} />
                  </button>
                  <button 
                    onClick={() => setViewMode('table')}
                    className={`p-2 rounded-lg transition-all ${viewMode === 'table' ? 'bg-[#bd9f67] text-gray-950 shadow-md font-semibold' : 'text-white/60 hover:text-white'}`}
                    title="List View"
                  >
                    <List size={18} />
                  </button>
                </div>
              </motion.div>
            )}

            {/* CONTENT AREA */}
            {filteredVideos.length === 0 ? (
              <div className="py-24 text-center">
                {searchTerm === '' ? (
                  <div className="fixed inset-0 w-screen h-screen bg-black/90 backdrop-blur-xl z-[9999] flex flex-col items-center justify-center">
                    {/* Glowing Loader Ring */}
                    <div className="relative w-20 h-20 mb-6">
                      <div className="absolute inset-0 rounded-full border-4 border-white/10" />
                      <div className="absolute inset-0 rounded-full border-t-4 border-[#bd9f67] animate-spin shadow-[0_0_20px_rgba(189,159,103,0.4)]" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <VideoIcon size={26} className="text-[#bd9f67] animate-pulse" />
                      </div>
                    </div>

                    <div className="text-center space-y-2">
                      <p className="text-white/90 text-lg font-semibold tracking-tight">
                        Fetching Video Library...
                      </p>
                      <p className="text-[#bd9f67]/80 text-xs tracking-[0.25em] uppercase font-medium">
                        Please wait a moment
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[#121318]/50 border border-white/10 rounded-2xl p-12 max-w-md mx-auto backdrop-blur-lg">
                    <Search className="mx-auto text-white/20 mb-3" size={40} />
                    <p className="text-white/80 font-medium text-base">No results found for "{searchTerm}"</p>
                    <p className="text-xs text-white/40 mt-1">Try searching with a different keyword or filter.</p>
                    <button 
                      onClick={() => setSearchTerm('')} 
                      className="mt-5 px-4 py-2 bg-[#bd9f67] text-gray-950 rounded-xl text-xs font-semibold hover:bg-[#d0b07a] transition shadow-md"
                    >
                      Clear Search
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {viewMode === 'card' ? (
                  <div className="grid gap-4 sm:gap-6 grid-cols-2 lg:grid-cols-4">
                    {currentVideos.map((v, i) => renderVideoCard(v, i))}
                  </div>
                ) : (
                  renderVideoTable(currentVideos)
                )}

                {/* PAGINATION CONTROLS */}
                {totalPages > 1 && (
                  <div className="mt-12 flex flex-col sm:flex-row justify-between items-center gap-4 text-white/60 pt-6 border-t border-white/10">
                    <p className="text-xs">
                      Showing <span className="text-[#bd9f67] font-semibold">{indexOfFirstVideo + 1}</span> to <span className="text-[#bd9f67] font-semibold">{Math.min(indexOfLastVideo, filteredVideos.length)}</span> of {filteredVideos.length} videos
                    </p>
                    <div className="flex gap-1.5 flex-wrap">
                      {pageNumbers.map(n => (
                        <button
                          key={n}
                          onClick={() => paginate(n)}
                          className={`px-3.5 py-1.5 text-xs font-medium rounded-lg transition-all ${
                            currentPage === n 
                              ? 'bg-[#bd9f67] text-gray-950 font-bold shadow-md' 
                              : 'bg-white/5 hover:bg-white/10 border border-white/5 text-white/80'
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* ELEGANT MODAL DIALOG */}
      <AnimatePresence>
        {selectedVideo && (
          <motion.div
            className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100] p-4 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedVideo(null)}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-5xl bg-[#121318] border border-white/15 rounded-2xl overflow-hidden shadow-[0_25px_50px_-12px_rgba(0,0,0,0.7)]"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header Bar */}
              <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-black/40">
                <div className="flex items-center gap-2 text-[#bd9f67] text-xs font-semibold tracking-wider uppercase">
                  <Sparkles size={14} /> Video Player
                </div>
                <button 
                  onClick={() => setSelectedVideo(null)}
                  className="p-1.5 rounded-full hover:bg-white/10 text-white/70 hover:text-white transition"
                  title="Close Modal"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Embed Screen */}
              <div className="aspect-video w-full bg-black">
                <iframe
                  width="100%" 
                  height="100%"
                  src={`https://www.youtube.com/embed/${extractYouTubeId(selectedVideo.url)}?autoplay=1`}
                  title={selectedVideo.name} 
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>

              {/* Modal Footer / Title */}
              <div className="p-6 bg-gradient-to-b from-[#121318] to-black">
                <h3 className="text-white text-lg sm:text-xl font-semibold leading-snug">
                  {selectedVideo.name}
                </h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}